import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { NoteActivityVersion, User } from '@paintscout/api'
import ClearIcon from '@material-ui/icons/Clear'
import moment from 'moment'
import { addAuth0Prefix, getActivityColor } from '@paintscout/util'
import linkify from 'linkifyjs/html'
import type { DialogProps } from '@paintscout/ui/components/Dialog'
import type { StyleClasses } from '@paintscout/ui/styles'
import { Dialog, DialogContent, DialogTitle, HtmlContent, IconButton, Typography } from '@paintscout/ui'
import EstimatorAvatar from '../components/EstimatorAvatar'
import ActivityContent from '../components/Activity/ActivityContent'

const useStyles = makeStyles<Theme, ViewVersionDialogProps>(
  (theme) => ({
    estimatorWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: theme.spacing(0.5),
      marginTop: theme.spacing(2),
      gap: theme.spacing(1.5)
    },
    fileBody: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: theme.spacing(1),
      paddingTop: theme.spacing(1)
    },
    activityRoot: {
      padding: 0
    },
    bubble: {
      position: 'relative',
      padding: theme.spacing(1.5, 1),
      '& a': {
        color: 'inherit'
      }
    },
    bubbleCorner: {
      position: 'absolute',
      top: 0,
      right: 0,
      borderStyle: 'solid',
      borderWidth: '8px',
      borderColor: `#fff #fff ${theme.paintscout.palette.brand.Neutral.P80} ${theme.paintscout.palette.brand.Neutral.P80}`,
      ['-moz-border-radius']: '0 0 0 5px',
      borderRadius: '0 0 0 5px'
    }
  }),
  { name: 'ViewVersionDialog' }
)

export interface ViewVersionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  version: NoteActivityVersion
  user: User
  original: boolean
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

function ViewVersionDialog(props: ViewVersionDialogProps) {
  const classes = useStyles(props)
  const { onCancel, version, user, original, ...baseDialogProps } = props

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle
        rightContent={
          <IconButton onClick={onCancel}>
            <ClearIcon />
          </IconButton>
        }
        classes={{ root: classes.dialogTitle }}
      >
        Viewing {original ? 'Original' : 'Edited'} Note
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <ActivityContent
          classes={{
            root: classes.activityRoot,
            bubble: classes.bubble
          }}
        >
          <div className={classes.bubbleCorner} />
          <HtmlContent content={linkify(version.note)} />
        </ActivityContent>
        <div className={classes.estimatorWrapper}>
          <EstimatorAvatar
            user={version.owner}
            className={classes.dropdownAvatar}
            userPictures={{
              [addAuth0Prefix(version.owner.id)]: user?.user_metadata?.customAvatar?.cdnValue ?? ''
            }}
            avatarColor={getActivityColor(user?.user_metadata?.color)}
            style={{
              paddingTop: 0
            }}
          />
          <Typography weight={'bold'}>{user?.name}</Typography>
          <Typography>
            {original ? 'created' : 'edited'} {moment(version.timestamp).fromNow()}
          </Typography>
        </div>
        {version.files && version.files.length > 0 && (
          <div className={classes.fileBody}>
            {version.files.map((file, index) => {
              return (
                <div key={index}>
                  <Typography variant="body2">
                    {file.name}
                    {file.type ? `.${file.type.includes('/') ? file.type.split('/')[1] : file.type}` : ''}
                  </Typography>
                </div>
              )
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ViewVersionDialog
